import {
  type BookedFlightItinerary,
  type AirBookingFlightItinerarySlice,
  type BookedFlightItineraryWithDepartureTime,
  type FlightItineraryResponse,
  TravelItineraryEnum,
} from "@b2bportal/air-booking-api";
import {
  type CacheableExternalResource,
  CallState,
  type ExternalResource,
} from "@b2bportal/core-types";
import type { FlightState } from "../../../store";
import { ALL_FLIGHT_STATES, flightsPostBookingApi } from "./api";

//  TODO show changed itinerary times if a minor schedule change exists
export const getDepartureSlice = (
  bookedItinerary: BookedFlightItinerary
): AirBookingFlightItinerarySlice => {
  switch (bookedItinerary.travelItinerary.TravelItinerary) {
    case TravelItineraryEnum.SingleTravelItinerary:
      return bookedItinerary.travelItinerary.slices[0];
    case TravelItineraryEnum.MultiTravelItinerary:
      return bookedItinerary.travelItinerary.travelItineraries[0].slices[0];
  }
};

export const getReturnSlice = (
  bookedItinerary: BookedFlightItinerary
): AirBookingFlightItinerarySlice | undefined => {
  switch (bookedItinerary.travelItinerary.TravelItinerary) {
    case TravelItineraryEnum.SingleTravelItinerary: {
      const slices = bookedItinerary.travelItinerary.slices;
      return slices.length > 1 ? slices[1] : undefined;
    }
    case TravelItineraryEnum.MultiTravelItinerary: {
      const itineraries = bookedItinerary.travelItinerary.travelItineraries;
      return itineraries.length > 1 ? itineraries[1].slices[0] : undefined;
    }
  }
};

export const getAirports = (state: FlightState) =>
  state.flights.flightPostBooking.context.airports;
export const getAirlines = (state: FlightState) =>
  state.flights.flightPostBooking.context.airlines;

type FlightItinerariesData = FlightItineraryResponse["itineraries"];

export const getFlightItineraries = (
  state: FlightState
): CacheableExternalResource<FlightItinerariesData, { error: string }> => {
  const result =
    flightsPostBookingApi.endpoints.fetchAllFlightItineraries.select({
      states: ALL_FLIGHT_STATES,
      referenceDateTime: "",
    })(state);

  if (result.isUninitialized) {
    return { state: CallState.NotCalled };
  }
  if (result.isSuccess) {
    return { state: CallState.Success, data: result.data.itineraries };
  }
  if (result.isLoading) {
    return { state: CallState.InProcess, data: result.data?.itineraries };
  }
  if (result.isError) {
    return {
      state: CallState.Failed,
      data: result.data?.itineraries,
      error: result.error as { error: string },
    };
  }
  return { state: CallState.NotCalled };
};

export const getSelectedFlightItinerary = (
  state: FlightState
): ExternalResource<
  BookedFlightItineraryWithDepartureTime,
  { error: string }
> => {
  const id = state.flights.flightPostBooking.selectedItineraryId;
  if (id == null) {
    return { state: CallState.NotCalled };
  }

  const result = flightsPostBookingApi.endpoints.fetchFlightItinerary.select({
    itineraryId: id,
    referenceDateTime: "",
  })(state);

  if (result.isUninitialized) {
    return { state: CallState.NotCalled };
  }
  if (result.isSuccess) {
    return { state: CallState.Success, data: result.data.itinerary };
  }
  if (result.isLoading) {
    return { state: CallState.InProcess };
  }
  if (result.isError) {
    return {
      state: CallState.Failed,
      error: result.error as { error: string },
    };
  }
  return { state: CallState.NotCalled };
};
